import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LabeledToggleSwitch } from '@/components/ui/LabeledToggleSwitch';
import { Modal } from '@/components/ui/Modal';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { inputClass, labelClass, listRowClass, inlineErrorClass } from '@/components/ui/inputStyles';
import { useSession } from '@/hooks/useSession';

const PER_PAGE = 15;

type Row = {
  id: number;
  public_id: string;
  name: string;
  enabled: boolean;
  created_at?: string;
  owner_username?: string | null;
  master_miniprogram_id?: number | null;
  master_name?: string | null;
  slave_count?: number;
  is_slave?: boolean;
};

export function MiniprogramsPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const onForwardLogsPage = location.pathname === '/robot-forward-logs';
  const { capabilities } = useSession();
  const isSysAdmin = capabilities.manage_users;
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addMasterId, setAddMasterId] = useState<number | ''>('');
  const [addMasterOptions, setAddMasterOptions] = useState<Row[]>([]);
  const [addMasterLoading, setAddMasterLoading] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const activeId = Number(searchParams.get('active_id') || 0);
  const activeViewRaw = searchParams.get('active_view');
  const activeView =
    activeViewRaw === 'logs'
      ? 'logs'
      : activeViewRaw === 'types' || activeViewRaw === 'content' || activeViewRaw === 'ads'
        ? activeViewRaw
        : 'config';

  const load = useCallback(async () => {
    setErr('');
    const r = await apiFetch<{ ok: boolean; items: Row[]; total: number; page: number; per_page: number }>(
      'list_miniprograms',
      { method: 'GET', query: { page, per_page: PER_PAGE } }
    );
    if (r.ok) {
      setItems(r.items);
      setTotal(r.total);
    } else {
      setErr('加载失败');
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMasterOptionsForAdd() {
    setAddMasterLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; items: Row[] }>('list_miniprograms', {
        method: 'GET',
        query: { page: 1, per_page: 200 },
      });
      setAddMasterOptions(r.ok ? (r.items || []).filter((x) => !x.is_slave) : []);
    } catch {
      setAddMasterOptions([]);
    } finally {
      setAddMasterLoading(false);
    }
  }

  function openAddModal() {
    setAddName('');
    setAddMasterId('');
    setAddOpen(true);
    void loadMasterOptionsForAdd();
  }

  async function submitAdd() {
    const n = addName.trim();
    if (!n) return;
    setAddSaving(true);
    setErr('');
    try {
      const body: { name: string; master_miniprogram_id?: number } = { name: n };
      if (addMasterId !== '' && addMasterId > 0) {
        body.master_miniprogram_id = addMasterId;
      }
      const r = await apiFetch<{ ok: boolean; id?: number; err?: string }>('create_miniprogram', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        setErr('创建失败：' + (r.err || 'unknown'));
        return;
      }
      setAddOpen(false);
      setAddName('');
      setAddMasterId('');
      setPage(1);
      await load();
    } finally {
      setAddSaving(false);
    }
  }

  async function toggleEnabled(row: Row) {
    const next = !row.enabled;
    if (
      !next &&
      !window.confirm(`确定停用小程序「${row.name}」？停用后小程序端将无法正常使用该配置。`)
    ) {
      return;
    }
    setTogglingId(row.id);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram', {
        method: 'POST',
        body: JSON.stringify({ id: row.id, enabled: next }),
      });
      if (!r.ok) {
        setErr(r.err === 'not_found' ? '小程序不存在或无权操作' : '更新启用状态失败');
        return;
      }
      await load();
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="小程序管理"
        description="配置展示站点、轮播、分类与信息；同一列表可进入「站点与转发配置」管理 Android 转发策略与队列。侧栏「机器人管理」维护转发机器人编号。"
        actions={
          <Link to="/robot-management">
            <Button type="button" variant="secondary">
              机器人管理
            </Button>
          </Link>
        }
      />
      {err ? <p className={inlineErrorClass}>{err}</p> : null}
      <Card
        title="小程序列表"
        titleExtra={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/robot-forward-logs">
              <Button type="button" variant="secondary">
                转发日志
              </Button>
            </Link>
            <Button type="button" variant="primary" onClick={() => openAddModal()}>
              新建小程序
            </Button>
          </div>
        }
      >
        {items.length === 0 ? (
          <p className="text-sm text-slate-600">暂无记录，请点击上方「新建小程序」。</p>
        ) : (
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {items.map((it) => {
              const slaveCount = Number(it.slave_count ?? 0);
              return (
              <li
                key={it.id}
                className={`${listRowClass} ${it.is_slave ? 'border-l-[3px] border-l-sky-400 bg-sky-50/40' : ''}`}
              >
                <div
                  className={`flex w-full flex-wrap items-start gap-3 md:flex-nowrap md:items-center ${it.is_slave ? 'pl-2' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      {it.is_slave ? (
                        <span className="rounded-md bg-sky-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                          从属
                        </span>
                      ) : slaveCount > 0 ? (
                        <span className="rounded-md bg-violet-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                          主集群
                        </span>
                      ) : (
                        <span className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                          独立
                        </span>
                      )}
                      {slaveCount > 0 && !it.is_slave ? (
                        <span className="text-xs text-violet-900/90">含 {slaveCount} 个从属小程序</span>
                      ) : null}
                      {it.is_slave && it.master_name ? (
                        <span className="text-xs text-slate-600">
                          隶属主小程序：<span className="font-medium text-slate-800">{it.master_name}</span>
                        </span>
                      ) : null}
                    </div>
                    <p className="font-medium text-slate-900">{it.name}</p>
                    <p className="mt-0.5 font-mono text-xs text-slate-500">
                      小程序 ID：<span className="select-all">{it.public_id}</span>
                    </p>
                    {isSysAdmin && it.owner_username ? (
                      <p className="mt-0.5 text-xs text-slate-400">归属：{it.owner_username}</p>
                    ) : null}
                    {it.created_at ? (
                      <p className="mt-1 text-xs text-slate-500">{it.created_at}</p>
                    ) : null}
                  </div>
                  <div className="ml-auto flex w-full flex-wrap items-center justify-end gap-2 md:w-auto md:flex-nowrap">
                    {(() => {
                      const isActiveRow = activeId === it.id;
                      const baseBtnCls =
                        'inline-flex min-w-[104px] justify-center rounded-lg border px-3 py-2 text-sm font-medium shadow-sm transition-all duration-150 hover:-translate-y-px active:translate-y-0';
                      const logsCls =
                        onForwardLogsPage && activeId === it.id
                          ? `${baseBtnCls} border-slate-900 bg-slate-900 text-white shadow-md hover:bg-slate-800`
                          : `${baseBtnCls} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
                      const configCls =
                        isActiveRow && activeView === 'config'
                          ? `${baseBtnCls} border-slate-900 bg-slate-900 text-white shadow-md hover:bg-slate-800`
                          : `${baseBtnCls} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
                      const typesCls =
                        isActiveRow && activeView === 'types'
                          ? `${baseBtnCls} border-slate-900 bg-slate-900 text-white shadow-md hover:bg-slate-800`
                          : `${baseBtnCls} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
                      const adsCls =
                        isActiveRow && activeView === 'ads'
                          ? `${baseBtnCls} border-slate-900 bg-slate-900 text-white shadow-md hover:bg-slate-800`
                          : `${baseBtnCls} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
                      const contentCls =
                        isActiveRow && activeView === 'content'
                          ? `${baseBtnCls} border-slate-900 bg-slate-900 text-white shadow-md hover:bg-slate-800`
                          : `${baseBtnCls} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
                      const usersCls = `${baseBtnCls} border-violet-200 bg-violet-50 text-violet-900 hover:border-violet-300 hover:bg-violet-100`;
                      const analyticsCls = `${baseBtnCls} border-indigo-200 bg-indigo-50 text-indigo-900 hover:border-indigo-300 hover:bg-indigo-100`;
                      const forwardCls = `${baseBtnCls} border-teal-200 bg-teal-50 text-teal-900 hover:border-teal-300 hover:bg-teal-100`;
                      const toggleBusy = togglingId === it.id;
                      return (
                        <>
                          <LabeledToggleSwitch
                            checked={it.enabled}
                            busy={toggleBusy}
                            aria-label={`${it.name}：${it.enabled ? '已开启' : '已关闭'}`}
                            onChange={() => void toggleEnabled(it)}
                          />
                          <Link
                            to={`/robot-forward-logs?miniprogram_id=${it.id}&active_id=${it.id}`}
                            className={logsCls}
                          >
                            转发日志
                          </Link>
                          <Link to={`/miniprograms/${it.id}?view=config`} className={configCls}>
                            参数配置
                          </Link>
                          <Link to={`/miniprograms/${it.id}?view=types`} className={typesCls}>
                            信息类型管理
                          </Link>
                          <Link to={`/miniprograms/${it.id}?view=ads`} className={adsCls}>
                            广告管理
                          </Link>
                          <Link to={`/miniprograms/${it.id}?view=content`} className={contentCls}>
                            信息管理
                          </Link>
                          <Link to={`/miniprograms/${it.id}/users`} className={usersCls}>
                            用户信息
                          </Link>
                          <Link to={`/miniprograms/${it.id}/analytics`} className={analyticsCls}>
                            访问统计
                          </Link>
                          <Link to={`/robot-miniprograms/${it.id}`} className={forwardCls}>
                            站点与转发配置
                          </Link>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </li>
              );
            })}
          </ul>
        )}
        <PaginationBar
          className="mt-4"
          page={page}
          totalPages={Math.max(1, Math.ceil(total / PER_PAGE))}
          total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(Math.max(1, Math.ceil(total / PER_PAGE)), p + 1))}
        />
      </Card>

      <Modal open={addOpen} title="新建小程序" onClose={() => !addSaving && setAddOpen(false)}>
        <div className="space-y-4">
          <div>
            <label className={labelClass} htmlFor="mp-name">
              名称
            </label>
            <input id="mp-name" className={inputClass} value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="如：门店展示小程序" />
          </div>
          <div>
            <label className={labelClass} htmlFor="mp-master">
              主从关系（可选）
            </label>
            <select
              id="mp-master"
              className={inputClass}
              disabled={addMasterLoading}
              value={addMasterId === '' ? '' : String(addMasterId)}
              onChange={(e) => {
                const v = e.target.value;
                setAddMasterId(v === '' ? '' : Number(v));
              }}
            >
              <option value="">独立小程序（可作为主，后续也可再指定从属）</option>
              {addMasterOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  从属于「{m.name}」（{m.public_id}）
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
              若选择主小程序，新建项将自动同步其<strong className="text-slate-700">信息类型定义</strong>；展示站点需在参数配置中勾选（且不能超出主小程序已选范围）。
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" disabled={addSaving} onClick={() => setAddOpen(false)}>
              取消
            </Button>
            <Button type="button" variant="primary" disabled={addSaving || !addName.trim() || addMasterLoading} onClick={() => void submitAdd()}>
              {addSaving ? '创建中…' : '创建'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
